import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fridaveAPI, roundsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ROW_HEIGHT = 44;
const ROW_OFFSET = 6;
const POLL_INTERVAL = 15000;

export default function FriDaveLanding() {
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardError, setLeaderboardError] = useState('');
  const [loading, setLoading] = useState(true);
  const [highlightStates, setHighlightStates] = useState({});
  const [rankChanges, setRankChanges] = useState({});
  const [scorePopStates, setScorePopStates] = useState({});
  const prevOrderRef = useRef({});
  const prevPointsRef = useRef({});
  const flashTimerRef = useRef(null);
  const scorePopTimerRef = useRef(null);

  const currentYear = String(new Date().getFullYear());

  useEffect(() => {
    fetchData();
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (scorePopTimerRef.current) clearTimeout(scorePopTimerRef.current);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLeaderboardError('');
      const rounds = await fridaveAPI.getRounds(currentYear).catch(() => []);
      const list = Array.isArray(rounds) ? rounds : [];

      const eligible = list.filter(r => r.status === 'in_progress' || r.status === 'active' || r.status === 'completed');
      const active = eligible.find(r => r.status === 'in_progress' || r.status === 'active');
      const mostRecent = active || eligible[0] || null;

      if (mostRecent) {
        setRound(mostRecent);
        try {
          const lb = await roundsAPI.getLeaderboard(mostRecent.id);
          const newLeaderboard = Array.isArray(lb) ? lb : [];
          const order = {};
          const points = {};
          newLeaderboard.forEach((p, i) => { order[p.player_id] = i; points[p.player_id] = p.total_points; });
          prevOrderRef.current = order;
          prevPointsRef.current = points;
          setLeaderboard(newLeaderboard);
        } catch (err) {
          setLeaderboardError(err.response?.data?.message || err.message || 'Failed to load leaderboard');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const pollLeaderboard = useCallback(async (currentRound) => {
    if (!currentRound) return;
    const lb = await roundsAPI.getLeaderboard(currentRound.id).catch(() => null);
    if (!lb) return;
    const newLeaderboard = Array.isArray(lb) ? lb : [];

    const newOrder = {};
    newLeaderboard.forEach((p, i) => { newOrder[p.player_id] = i; });

    const newChanges = {};
    const newScorePops = {};
    newLeaderboard.forEach((p) => {
      const oldIdx = prevOrderRef.current[p.player_id];
      const newIdx = newOrder[p.player_id];
      if (oldIdx !== undefined && oldIdx !== newIdx) {
        newChanges[p.player_id] = oldIdx - newIdx;
      }
      if (prevPointsRef.current[p.player_id] !== undefined &&
          prevPointsRef.current[p.player_id] !== p.total_points) {
        newScorePops[p.player_id] = true;
      }
    });

    prevPointsRef.current = Object.fromEntries(newLeaderboard.map(p => [p.player_id, p.total_points]));

    // Score pop — fire immediately, clear after animation
    if (Object.keys(newScorePops).length > 0) {
      if (scorePopTimerRef.current) clearTimeout(scorePopTimerRef.current);
      setScorePopStates(newScorePops);
      scorePopTimerRef.current = setTimeout(() => setScorePopStates({}), 600);
    }

    if (Object.keys(newChanges).length > 0) {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);

      // Phase 1: highlight rows about to move
      setHighlightStates(newChanges);

      // Phase 2: after 300ms trigger slide
      flashTimerRef.current = setTimeout(() => {
        prevOrderRef.current = newOrder;
        setLeaderboard(newLeaderboard);
        setHighlightStates({});
        setRankChanges(newChanges);

        // Phase 3: clear after slide completes (550ms + buffer)
        flashTimerRef.current = setTimeout(() => {
          setRankChanges({});
        }, 750);
      }, 300);
    } else {
      prevOrderRef.current = newOrder;
      setLeaderboard(newLeaderboard);
    }
  }, []);

  useEffect(() => {
    if (!round || !(round.status === 'in_progress' || round.status === 'active')) return;
    const interval = setInterval(() => pollLeaderboard(round), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [round, pollLeaderboard]);

  const isLive = round && (round.status === 'in_progress' || round.status === 'active');
  const isCompleted = round && round.status === 'completed';

  const sortedLeaderboard = [
    ...leaderboard.filter(p => parseInt(p.holes_completed) > 0),
    ...leaderboard.filter(p => !(parseInt(p.holes_completed) > 0)).sort((a, b) => a.player_name.localeCompare(b.player_name)),
  ];

  // Live: show ties when points + holes_completed are equal
  // Completed: countback has resolved the final order — positions are sequential, no T prefix
  let leaderboardWithPos;
  if (isLive) {
    const withPos = [];
    sortedLeaderboard.forEach((player, index) => {
      if (index === 0) {
        withPos.push({ ...player, pos: 1, isTied: false });
      } else {
        const prev = sortedLeaderboard[index - 1];
        const isTied = parseInt(player.holes_completed) > 0 &&
          player.total_points === prev.total_points;
        const pos = isTied ? withPos[index - 1].pos : index + 1;
        if (isTied && !withPos[index - 1].isTied) {
          withPos[index - 1] = { ...withPos[index - 1], isTied: true };
        }
        withPos.push({ ...player, pos, isTied });
      }
    });
    leaderboardWithPos = withPos;
  } else {
    leaderboardWithPos = sortedLeaderboard.map((player, index) => ({ ...player, pos: index + 1 }));
  }

  const showCountback = isCompleted &&
    leaderboardWithPos.length >= 2 &&
    leaderboardWithPos[0].total_points === leaderboardWithPos[1].total_points;

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div className="px-4 pb-4">
      {/* Status card */}
      {round ? (
        <div className={`rounded-2xl p-4 mb-4 border ${isLive ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-0.5">
                {isLive ? 'Round in Progress' : 'Most Recent Round'}
              </p>
              <h2 className="text-lg font-bold text-fd-primary">{round.event_name}</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date(round.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            {isLive && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full animate-pulse">LIVE</span>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200 mb-4">
          <div className="text-5xl mb-3">⛳</div>
          <p className="text-gray-600 font-semibold">No rounds yet this season</p>
        </div>
      )}

      {/* Leaderboard error */}
      {leaderboardError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
          {leaderboardError}
        </div>
      )}

      {/* Leaderboard */}
      {leaderboardWithPos.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gray-100 px-4 py-2 flex items-center text-xs font-semibold text-gray-700 uppercase tracking-wide">
            <div className="w-12 shrink-0 text-center">Pos</div>
            <div className="flex-1 min-w-0">Player</div>
            <div className="w-16 shrink-0 text-center">Pts</div>
            <div className="w-16 shrink-0 text-center">Thru</div>
          </div>

          {isLive ? (
            /* Animated absolute-position rows for live rounds */
            <div className="leaderboard-container" style={{ height: leaderboardWithPos.length * ROW_HEIGHT + ROW_OFFSET }}>
              {leaderboardWithPos.map((player, index) => {
                const change = rankChanges[player.player_id] || 0;
                const isHighlighted = !!highlightStates[player.player_id];
                const isMoving = !!rankChanges[player.player_id];
                const extraClass = isMoving ? 'leaderboard-row--moving' : isHighlighted ? 'leaderboard-row--highlight' : '';
                return (
                  <button
                    key={player.player_id}
                    onClick={() => navigate(`/fridave/rounds/${round.id}/players/${player.player_id}`)}
                    className={`leaderboard-row px-4 flex items-center border-b border-gray-100 text-left w-full bg-white hover:bg-gray-50 ${extraClass}`}
                    style={{ top: index * ROW_HEIGHT + ROW_OFFSET, height: ROW_HEIGHT }}
                  >
                    <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
                      {parseInt(player.holes_completed) > 0 ? <>{player.isTied ? <span className="text-xs">T</span> : null}{player.pos}</> : <span className="text-gray-300">—</span>}
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
                      <span className="font-bold text-gray-900 truncate" style={{ fontSize: '20px' }}>{player.player_name}</span>
                      {change > 0 && (
                        <svg className="w-3 h-3 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                      {change < 0 && (
                        <svg className="w-3 h-3 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                    <div className="w-16 shrink-0 text-center font-bold text-red-600" style={{ fontSize: '20px' }}>
                      <span className={scorePopStates[player.player_id] ? 'score-pop' : ''}>{player.total_points}</span>
                    </div>
                    <div className="w-16 shrink-0 text-center text-sm text-gray-500">{player.holes_completed}</div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Static rows for completed/other rounds */
            leaderboardWithPos.map((player, index) => (
              <React.Fragment key={player.round_player_id}>
                <button
                  onClick={() => navigate(`/fridave/rounds/${round.id}/players/${player.player_id}`)}
                  className="w-full px-4 py-3 flex items-center border-b border-gray-100 last:border-0 bg-white hover:bg-gray-50 text-left"
                >
                  <div className="w-12 shrink-0 text-center text-sm font-semibold text-gray-600">
                    {parseInt(player.holes_completed) > 0 ? <>{player.isTied ? <span className="text-xs">T</span> : null}{player.pos}</> : <span className="text-gray-300">—</span>}
                  </div>
                  <div className="flex-1 min-w-0 font-bold text-gray-900 truncate" style={{ fontSize: '20px' }}>{player.player_name}</div>
                  <div className="w-16 shrink-0 text-center font-bold text-red-600" style={{ fontSize: '20px' }}>{player.total_points}</div>
                  <div className="w-16 shrink-0 text-center text-sm text-gray-500">{player.holes_completed}</div>
                </button>
                {showCountback && index === 0 && (
                  <div className="px-4 py-1.5 text-center text-xs text-gray-400 italic bg-gray-50 border-b border-gray-100">
                    Winner on countback
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>
      )}
    </div>
  );
}
